import { useEffect, useState } from "react";
import ControlPanel from "./customizer/ControlPanel";
import SimplePreview from "./customizer/SimplePreview";
import PreviewPanel from "./customizer/preview/PreviewPanel";
import PillTabBar from "./customizer/PillTabBar";
import { useMapStore } from "@/store/useMapStore";
import { mapArtConfig } from "@/config/customizerConfig";
import { fetchPalettes } from "@/services/paletteService";
import { useIsMobile } from "@/hooks/use-mobile";
import { useProductVariants } from "@/hooks/useProductVariants";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Settings } from "lucide-react";

const getTabFromHash = () => {
  const match = window.location.hash.match(/\/editor\/(\w+)/);
  return match ? match[1] : "map";
};

const MapCustomizer = () => {
  const isMobile = useIsMobile();
  const { setLocation } = useMapStore();
  const [activeTab, setActiveTab] = useState(getTabFromHash);

  const productHandle = import.meta.env.VITE_SHOPIFY_PRODUCT_HANDLE || "3d-custom-map-art";
  useProductVariants(productHandle);

  // Initialize default location
  useEffect(() => {
    const currentLocation = useMapStore.getState().location;
    if (mapArtConfig.defaultLocation && !currentLocation) {
      setLocation(mapArtConfig.defaultLocation);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch palettes from API on mount (one-time)
  useEffect(() => {
    const state = useMapStore.getState();
    if (state.palettes.length === 0) {
      fetchPalettes().then(palettes => {
        const currentState = useMapStore.getState();
        if (currentState.palettes.length === 0) {
          currentState.setPalettes(palettes);
          if (!currentState.hasCustomColors) {
            const defaultPalette = palettes.find(p => p.id === 'sage_cream') ?? palettes[0];
            if (defaultPalette) currentState.setPalette(defaultPalette.id);
          }
        }
      });
    }
  }, []);

  // Sync PillTabBar with hash changes (browser back/forward)
  useEffect(() => {
    const handleHashChange = () => setActiveTab(getTabFromHash());
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    window.location.hash = `#/editor/${tab}`;
  };

  // ─── Mobile layout ───
  if (isMobile) {
    return (
      <div
        className="relative w-full h-screen"
        style={{ backgroundColor: "var(--brand-bg)", fontFamily: "var(--font-body)" }}
      >
        <SimplePreview config={mapArtConfig} fullscreen />
        <Sheet>
          <SheetTrigger asChild>
            <Button
              size="lg"
              className="absolute bottom-4 right-4 shadow-xl"
              style={{ backgroundColor: "var(--sage-400)" }}
            >
              <Settings className="mr-2 h-4 w-4" />
              Customize
            </Button>
          </SheetTrigger>
          <SheetContent side="bottom" className="h-[85vh]">
            <div className="p-4">
              <PillTabBar activeTab={activeTab} onTabChange={handleTabChange} />
            </div>
            <ControlPanel config={mapArtConfig} />
          </SheetContent>
        </Sheet>
      </div>
    );
  }

  // ─── Desktop: customizer card only ───
  return (
    <div
      className="max-w-6xl mx-auto rounded-2xl border shadow-sm overflow-hidden"
      style={{
        borderColor: "var(--brand-border)",
        backgroundColor: "var(--brand-bg)",
        fontFamily: "var(--font-body)",
      }}
    >
      {/* Pill Tab Navigation */}
      <div className="px-6 pt-6 pb-0">
        <div className="pb-4">
          <PillTabBar activeTab={activeTab} onTabChange={handleTabChange} />
        </div>
      </div>

      {/* Content: Left Panel + Right Preview */}
      <div className="grid lg:grid-cols-[380px_1fr]" style={{ minHeight: "520px" }}>
        {/* Left: Control Panel + CTAs */}
        <div
          className="border-r overflow-y-auto flex flex-col"
          style={{
            borderColor: "var(--brand-border)",
            maxHeight: "620px",
          }}
        >
          <div className="flex-1">
            <ControlPanel config={mapArtConfig} />
          </div>
        </div>

        {/* Right: Preview Area */}
        <div
          className="relative flex items-center justify-center overflow-hidden"
          style={{
            background: "linear-gradient(to bottom right, var(--sage-50), var(--brand-bg))",
            minHeight: "520px",
          }}
        >
          <div className="w-full h-full">
            <PreviewPanel config={mapArtConfig} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapCustomizer;
